import React, { useState, useEffect } from 'react';
import { Card, Button, Spinner, Alert } from 'react-bootstrap';
import { ArrowUpRight, Download, ExclamationTriangleFill } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import subscriptionService from '../services/subscriptionService';
import { UserSubscription } from '../types/subscription.types';
import './SubscriptionManagement.css';

interface SubscriptionManagementProps {
  onDownloadSuccess?: () => void;
}

const SubscriptionManagement: React.FC<SubscriptionManagementProps> = ({ onDownloadSuccess }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [downloadingData, setDownloadingData] = useState(false);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      const data = await subscriptionService.getSubscriptionStatus();
      setSubscription(data);
    } catch (err: any) {
      console.error('Error fetching subscription:', err);
      setError('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePlan = () => {
    navigate('/select-plan');
  };

  const handleUpdateBillingMethod = async () => {
    try {
      setActionLoading(true);
      setError(null);

      const { url } = await subscriptionService.createPortalSession({
        returnUrl: window.location.href,
      });

      window.location.href = url;
    } catch (err: any) {
      console.error('Error creating portal session:', err);
      setError(err.response?.data?.error || 'Failed to open billing portal');
      setActionLoading(false);
    }
  };

  const getPlanDisplayName = (planName: string) => {
    const displayNames: Record<string, string> = {
      starter: 'Starter',
      pro: 'Early Adopter',
      ultra: 'Studio',
    };
    return (displayNames[planName] || planName.charAt(0).toUpperCase() + planName.slice(1)) + ' Plan';
  };

  const formatPrice = (amount?: number, currency?: string, interval?: string) => {
    if (!amount || !currency) return 'Free';

    // Convert from cents to dollars
    const dollars = amount / 100;

    // Format currency
    const formattedAmount = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(dollars);

    // Add interval
    const intervalText = interval === 'year' ? 'year' : 'month';

    return `Charged at ${formattedAmount}/${intervalText}`;
  };

  const formatCancelledBillingInfo = (
    periodEnd: Date | string | null | undefined | { _seconds?: number; seconds?: number },
    amount?: number,
    currency?: string,
    interval?: string
  ) => {
    if (!periodEnd) return 'Subscription cancelled';

    let endDate: Date;

    // Handle Firestore Timestamp objects
    if (typeof periodEnd === 'object' && periodEnd !== null && '_seconds' in periodEnd && periodEnd._seconds) {
      endDate = new Date(periodEnd._seconds * 1000);
    } else if (typeof periodEnd === 'object' && periodEnd !== null && 'seconds' in periodEnd && periodEnd.seconds) {
      endDate = new Date(periodEnd.seconds * 1000);
    } else {
      // Handle string or Date
      endDate = new Date(periodEnd as string | Date);
    }

    // Check if date is valid
    if (isNaN(endDate.getTime())) {
      return 'Subscription cancelled';
    }

    const formattedDate = endDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });

    // Format the price if available
    if (amount && currency) {
      const dollars = amount / 100;
      const formattedAmount = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency.toUpperCase(),
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(dollars);
      const intervalText = interval === 'year' ? 'year' : 'month';
      return `Charged at ${formattedAmount}/${intervalText}, plan ends on ${formattedDate}`;
    }

    return `Plan ends on ${formattedDate}`;
  };

  if (loading) {
    return (
      <Card className="subscription-card mb-4">
        <Card.Body className="subscription-loading text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </Card.Body>
      </Card>
    );
  }

  if (!subscription) {
    return null;
  }

  const { subscription: subData } = subscription;

  const handleDownloadAccountData = async () => {
    setDownloadingData(true);
    setError('');

    try {
      const token = await currentUser?.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/exports/account-data`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 404) {
          throw new Error('No data to export. Create some projects or conversations first.');
        }
        throw new Error(errorData.error || 'Failed to download account data');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      link.download =
        filenameMatch?.[1] || `revealer-data-${new Date().toISOString().split('T')[0]}.zip`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      if (onDownloadSuccess) {
        onDownloadSuccess();
      }
    } catch (error: any) {
      console.error('Download error:', error);
      setError(error.message || 'Failed to download account data. Please try again.');
    } finally {
      setDownloadingData(false);
    }
  };

  return (
    <Card className="subscription-card mb-4">
      <Card.Header>
        <h5 className="card-title">Plan and Billing</h5>
      </Card.Header>
      <Card.Body>
        {error && (
          <Alert
            variant="danger"
            onClose={() => setError(null)}
            dismissible
            className="subscription-alert mb-3"
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            variant="success"
            onClose={() => setSuccess(null)}
            dismissible
            className="subscription-alert mb-3"
          >
            {success}
          </Alert>
        )}

        {/* Current Subscription Row */}
        <div className="subscription-row">
          <div className="subscription-info-inline">
            <span className="subscription-label">Current Subscription</span>
            <span className="subscription-value">{getPlanDisplayName(subData.planName)}</span>
          </div>
          <Button
            variant="link"
            className="subscription-action-link"
            onClick={handleUpdatePlan}
            disabled={actionLoading}
          >
            {subData.cancelAtPeriodEnd ? 'RENEW PLAN' : 'UPDATE PLAN'}&nbsp;
            <ArrowUpRight size={16} className="ms-1" />
          </Button>
        </div>

        {/* Billing Info Row */}
        <div className="subscription-row">
          <div className="subscription-info-inline">
            <span className="subscription-label">Billing Info</span>
            <span className="subscription-value">
              {subData.cancelAtPeriodEnd
                ? formatCancelledBillingInfo(subData.currentPeriodEnd, subData.amount, subData.currency, subData.interval)
                : formatPrice(subData.amount, subData.currency, subData.interval)}
            </span>
          </div>
          {subData.planName !== 'starter' && (
            <Button
              variant="link"
              className="subscription-action-link"
              onClick={handleUpdateBillingMethod}
              disabled={actionLoading}
            >
              {actionLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    className="me-2"
                  />
                  Loading...
                </>
              ) : (
                <>
                  UPDATE BILLING METHOD&nbsp;
                  <ArrowUpRight size={16} className="ms-1" />
                </>
              )}
            </Button>
          )}
        </div>

        {/* Download Data Row */}
        <div className="subscription-row">
          <div className="subscription-info-inline">
            <span className="subscription-label">Account Data</span>
            <span className="subscription-value">Download account data</span>
          </div>
          <Button
            variant="link"
            className="subscription-action-link"
            size="sm"
            onClick={handleDownloadAccountData}
            disabled={downloadingData}
          >
            {downloadingData ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Preparing...
              </>
            ) : (
              <>
                Download all account data&nbsp;
                <Download className="me-1" /> 
              </>
            )}
          </Button>
        </div>

        {/* Cancel/Reactivate Subscription */}
        {subData.planName !== 'starter' && (
          <div className="subscription-cancel-section">
            {subData.cancelAtPeriodEnd ? (
              <button
                className="subscription-cancel-link reactivate"
                onClick={() => navigate('/cancel-account')}
                disabled={actionLoading}
              >
                REACTIVATE SUBSCRIPTION OR DELETE ACCOUNT
              </button>
            ) : (
              <button
                className="subscription-cancel-link"
                onClick={() => navigate('/cancel-account')}
                disabled={actionLoading}
              >
                CANCEL OR DELETE ACCOUNT
              </button>
            )}
          </div>
        )}

        {/* Cancelled Subscription Warning */}
        {subData.cancelAtPeriodEnd && (
          <div className="subscription-warning-box">
            <div className="subscription-warning-header">
              <ExclamationTriangleFill className="subscription-warning-icon" />
              <span className="subscription-warning-title">Attention: Data may be affected</span>
            </div>
            <p className="subscription-warning-text">
              By downgrading your account, you may need to delete projects and/or artifacts that are over your new capacity limits. You will have until the end of the billing cycle to download anything you’d like to save before they are automatically deleted, starting with the most recent.
            </p>
            <button
              className="subscription-warning-download-btn"
              onClick={handleDownloadAccountData}
              disabled={downloadingData}
            >
              {downloadingData ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Preparing...
                </>
              ) : (
                <>
                  <Download className="me-2" />
                  DOWNLOAD ALL ACCOUNT DATA
                </>
              )}
            </button>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default SubscriptionManagement;
